/** A predecessor/successor tie chip. Clicking it opens the SAME rich editor the
 *  board uses (DepChipTypeahead) — search to replace the tie, change its type /
 *  lag, or delete it — instead of a separate simple popover. */
import React from "react";
import { autoFontColor } from "../../../../utils/color";
import { formatLagSuffix, predTypeLabel } from "../../../../utils/p6";
import type { PredType } from "../../../../utils/p6";
import { predTypeFromShort, shortFromPredType } from "../../../../models";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import DepChipTypeahead from "../DepChipTypeahead/DepChipTypeahead";
import type { DepCandidate, ExternalCandidate } from "../DepChipTypeahead/DepChipTypeahead";
import "./DepChip.css";

interface Props {
  workCode: string;
  workName: string;
  /** Full P6 task_name, shown on hover (workName is the short board label). */
  fullName?: string;
  /** P6 activity code (unique) — shown only in the hover tooltip. */
  taskCode?: string;
  zoneLabel: string | null;        // e.g. "L3"; null if no disambiguator needed
  color: string;
  predType: PredType;
  lagHours: number;
  /** This tie is the binding (driving) dependency — marked so the chip that
   *  actually sets the date is obvious. */
  driving?: boolean;
  /** Compact = the table pill: code + relationship only (no name / zone). The
   *  full name still lives in the hover tooltip and the Relationships panel. */
  compact?: boolean;
  /** "pill" (default) = the filled table chip. "row" = the Relationships-panel
   *  chip: a color dot + code + full name + relationship, on a subtle ground. */
  variant?: "pill" | "row";
  /** Retype / re-lag the SAME tie (no new pick). */
  onChangeRel: (type: PredType, lagDays: number) => void;
  onDelete: () => void;
  /** Pickable activities to RE-POINT this tie at (caller-filtered). */
  candidates?: DepCandidate[];
  /** The current tie partner's graph id — its row starts checked in the picker. */
  currentId?: string;
  /** Re-point the tie at a different activity (with its type / lag). */
  onReplace?: (newPredId: string, type: PredType, lagDays: number) => void;
  /** Server-driven schedule search, shown under "External activities". */
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  /** Re-point this tie at a searched activity (closes the popover). */
  onPickExternal?: (external: ExternalCandidate) => void;
}

const DepChip: React.FC<Props> = ({
  workCode,
  workName,
  fullName,
  taskCode,
  zoneLabel,
  color,
  predType,
  lagHours,
  driving,
  compact = false,
  variant = "pill",
  onChangeRel,
  onDelete,
  candidates,
  currentId,
  onReplace,
  fetchExternals,
  onPickExternal,
}) => {
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const lagSuffix = formatLagSuffix(lagHours);

  return (
    <span className="dep-chip-wrap">
      <Tooltip content={`${taskCode ? taskCode + " · " : ""}${fullName || workName} · ${predTypeLabel(predType)}${lagSuffix}`}>
        <button
          ref={buttonRef}
          type="button"
          className={
            `dep-chip dep-chip--${variant}` +
            (compact ? " dep-chip--compact" : "") +
            (color ? "" : " dep-chip--untagged") +
            (driving ? " dep-chip--driving" : "")
          }
          style={
            color ? { background: color, color: autoFontColor(color) } : undefined
          }
          onClick={() => setOpen((o) => !o)}
        >
          {driving && <span className="dep-chip-driving-mark" aria-hidden="true">▸</span>}
          <span className="dep-chip-code">{workCode}</span>
          {!compact && <span className="dep-chip-name">{workName}</span>}
          {!compact && zoneLabel && <span className="dep-chip-zone">{zoneLabel}</span>}
          <span className="dep-chip-rel">
            {predTypeLabel(predType)}{lagSuffix}
          </span>
        </button>
      </Tooltip>
      {open && (
        <DepChipTypeahead
          candidates={candidates ?? []}
          fetchExternals={fetchExternals}
          onPickExternal={
            onPickExternal
              ? (ext) => {
                  setOpen(false);
                  onPickExternal(ext);
                }
              : undefined
          }
          anchorRef={buttonRef}
          onClose={() => setOpen(false)}
          withRel={{
            currentId: currentId ?? null,
            initialType: shortFromPredType(predType),
            initialLagDays: lagHours / 8,
            commitLabel: "Update",
            onCommit: (candidate, type, lagDays) => {
              const pt = predTypeFromShort(type);
              if (candidate && candidate.instanceId !== currentId && onReplace) {
                onReplace(candidate.instanceId, pt, lagDays);
              } else {
                onChangeRel(pt, lagDays);
              }
            },
            onRemove: onDelete,
          }}
        />
      )}
    </span>
  );
};

export default DepChip;
